import React, { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { BeatLoader } from "react-spinners";
import {
  fetchTransferRelease,
  TransferInstallerDto,
  TransferReleaseDto,
} from "../../api/transferApi";
import { formatBytes } from "../common/helpers";
import { WarningIcon } from "../common/ProjectIcons";
import tailwindClassnames from "../common/TailwindClassnames";

/** The installer for a platform's button, plus the ones offered as alternatives. */
export type PlatformDownloads = {
  recommended: TransferInstallerDto;
  alternatives: TransferInstallerDto[];
};

type Platform = TransferInstallerDto["platform"];

const PLATFORMS: Platform[] = ["windows", "macos"];

/**
 * Group the API's flat installer list per platform.
 *
 * The API sends the recommended installer first within a platform, but this does not
 * rely on order: `recommended` is read from the flag, and a platform whose entries are
 * all non-recommended still gets a button (its first entry) rather than nothing. Platforms
 * the page does not know are ignored, so a future Linux entry changes nothing here.
 * @param installers the installers as the API returned them
 * @returns one entry per platform that has at least one installer
 */
export function groupInstallers(
  installers: TransferInstallerDto[],
): Partial<Record<Platform, PlatformDownloads>> {
  const grouped: Partial<Record<Platform, PlatformDownloads>> = {};
  for (const platform of PLATFORMS) {
    const forPlatform = installers.filter((i) => i.platform === platform);
    if (forPlatform.length === 0) continue;
    const recommended =
      forPlatform.find((i) => i.recommended) ?? forPlatform[0];
    grouped[platform] = {
      recommended,
      alternatives: forPlatform.filter((i) => i !== recommended),
    };
  }
  return grouped;
}

const downloadButtonClass = `${tailwindClassnames.btnPrimary} inline-block`;
const inlineLinkClass =
  "text-sm font-semibold text-storm-blue hover:underline dark:text-sky-blue-light";

/**
 * Akili Transfer download page (AKL-803) — the "I don't have the app yet" path.
 *
 * Page owner: Margaux Armfield. The strings here were approved verbatim on 2026-09-09
 * and deliberately name no publisher, so they stay true before and after Windows code
 * signing (AKL-767) lands.
 *
 * Everything in the download card comes from `GET /transfer/release`, which the API
 * builds from the desktop app's auto-update manifest — so a new release appears here
 * with no edit. The card is never empty: it is loading, it shows the installers, or it
 * shows the failure sentence. The two copy cards below it are static and always render,
 * so a researcher still learns what the app is and what to expect on first run even
 * while the download host is unreachable.
 */
const TransferDownloadPage: React.FC = () => {
  const { t } = useTranslation();
  const [release, setRelease] = useState<TransferReleaseDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchTransferRelease()
      .then((data) => {
        if (cancelled) return;
        setRelease(data);
        setFailed(false);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const downloads = release ? groupInstallers(release.installers) : {};
  const windows = downloads.windows;
  const macos = downloads.macos;
  const windowsMsi = windows?.alternatives.find((i) => i.format === "msi");

  /**
   * "Version 0.1.1 · 27 MB · Released 09/03/2026". Without the size segment when storage
   * gave no length — a "·  ·" gap would read as broken.
   */
  const metaLine = (
    current: TransferReleaseDto,
    installer: TransferInstallerDto,
  ): string => {
    const date = new Date(current.published_at).toLocaleDateString();
    if (installer.size_bytes === null) {
      return t("Version {{version}} · Released {{date}}", {
        version: current.version,
        date,
      });
    }
    return t("Version {{version}} · {{size}} · Released {{date}}", {
      version: current.version,
      size: formatBytes(installer.size_bytes),
      date,
    });
  };

  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-6 text-left">
      <div className={tailwindClassnames.pageHeaderRow}>
        <div>
          <h1 className={tailwindClassnames.pageTitle}>
            {t("Akili Transfer")}
          </h1>
          <p className={tailwindClassnames.pageSubtitle}>
            {t(
              "The desktop app for moving large numbers of images between your computer and akili.",
            )}
          </p>
        </div>
      </div>

      <div className={tailwindClassnames.formSectionStack}>
        <section
          className={tailwindClassnames.formSectionCard}
          data-testid="transfer-download-card"
        >
          <div className={tailwindClassnames.formSectionHeader}>
            <h2 className={tailwindClassnames.formSectionTitle}>
              {t("Download")}
            </h2>
          </div>

          {loading && (
            <div className="flex items-center gap-3" aria-live="polite">
              <BeatLoader size={8} color={tailwindClassnames.stormBlue} />
              <span className={tailwindClassnames.basicLabel}>
                {t("Checking the latest version…")}
              </span>
            </div>
          )}

          {!loading && failed && (
            <p className={tailwindClassnames.warningLabel} aria-live="polite">
              {t(
                "The download is not available right now. Please try again in a few minutes.",
              )}
            </p>
          )}

          {!loading && release && (
            <div className="flex flex-col gap-5">
              {windows && (
                <div className="flex flex-col gap-2">
                  <div>
                    <a
                      href={windows.recommended.url}
                      rel="noopener"
                      className={downloadButtonClass}
                      data-testid="transfer-download-windows"
                    >
                      {t("Download for Windows")}
                    </a>
                  </div>
                  <p className={tailwindClassnames.helperText}>
                    {metaLine(release, windows.recommended)}
                  </p>
                  <p
                    className={tailwindClassnames.helperText}
                    data-testid="transfer-download-windows-file-name"
                  >
                    {windows.recommended.file_name}
                  </p>
                  {windowsMsi && (
                    <p>
                      <a
                        href={windowsMsi.url}
                        rel="noopener"
                        className={inlineLinkClass}
                        data-testid="transfer-download-windows-msi"
                      >
                        {t("Also available as an MSI installer")}
                      </a>
                    </p>
                  )}
                </div>
              )}

              {macos ? (
                <div className="flex flex-col gap-2">
                  <div>
                    <a
                      href={macos.recommended.url}
                      rel="noopener"
                      className={downloadButtonClass}
                      data-testid="transfer-download-macos"
                    >
                      {t("Download for macOS")}
                    </a>
                  </div>
                  <p className={tailwindClassnames.helperText}>
                    {metaLine(release, macos.recommended)}
                  </p>
                  <p className={tailwindClassnames.helperText}>
                    {macos.recommended.file_name}
                  </p>
                </div>
              ) : (
                <p
                  className={tailwindClassnames.helperText}
                  data-testid="transfer-macos-unavailable"
                >
                  {t("A macOS version is not available yet.")}
                </p>
              )}
            </div>
          )}
        </section>

        <section className={tailwindClassnames.formSectionCard}>
          <div className={tailwindClassnames.formSectionHeader}>
            <h2 className={tailwindClassnames.formSectionTitle}>
              {t("What it does")}
            </h2>
          </div>
          <div className="flex flex-col gap-2">
            <p className={tailwindClassnames.basicLabel}>
              {t(
                "Use it when an export has too many images to download in your browser, or when you have a large folder of images to upload.",
              )}
            </p>
            <p className={tailwindClassnames.basicLabel}>
              {t("You sign in with the same akili account you use here.")}
            </p>
          </div>
        </section>

        {/* Styled as a warning on purpose (Margaux, 2026-09-09: "option B"): the
            SmartScreen prompt is the moment a researcher decides whether to trust us,
            so this card must not read as one more paragraph. Same primary-orange
            family the export panel and form errors use; the copy is unchanged, only
            split at sentence boundaries so the key phrases can carry weight. */}
        <section
          className="overflow-hidden rounded-[10px] border border-primary-orange bg-white shadow-sm transition duration-300 dark:border-primary-orange/60 dark:bg-storm-blue-card"
          data-testid="transfer-first-run-card"
        >
          <div className="flex items-center gap-2.5 border-b border-primary-orange/45 bg-primary-orange/10 px-6 py-3.5 dark:bg-primary-orange/20">
            <WarningIcon />
            <h2 className="text-[15px] font-semibold text-primary-orange-dark transition duration-300 dark:text-primary-orange-light">
              {t("The first time you run it")}
            </h2>
          </div>
          <div className="flex flex-col gap-2 px-6 pb-3 pt-5">
            <p className={tailwindClassnames.basicLabel}>
              <strong className="font-semibold">
                {t("Windows may show a message that it protected your PC.")}
              </strong>{" "}
              {t(
                "This happens because the app is new and has not yet built up a reputation with Microsoft; it is expected.",
              )}
            </p>
            <p className={tailwindClassnames.basicLabel}>
              <Trans
                i18nKey="To continue, choose <bold>More info</bold>, then <bold>Run anyway</bold>."
                components={{ bold: <strong className="font-semibold" /> }}
              />{" "}
              <strong className="font-semibold text-primary-orange-dark dark:text-primary-orange-light">
                {t("Only do this for a file you downloaded from this page.")}
              </strong>
            </p>
            <p className={tailwindClassnames.basicLabel}>
              {t(
                "Once installed, the app checks for updates when it starts and asks before installing them.",
              )}
            </p>
          </div>
        </section>
      </div>
    </div>
  );
};

export default TransferDownloadPage;
